import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import productData, { siteConfig } from "../Datas/productData";
import { CartContext } from "./CartContext";
import "../css/productdetails.scss";

const COLOUR_VALUES = {
  "off white": "#eeeade",
  "antique white": "#e9e2d2",
  "all white": "#f7f7f4",
  white: "#f4f4f0",
  black: "#181818",
  beige: "#cbbda7",
  tea: "#a18a6c",
  taupe: "#9b8877",
  cream: "#dfd3bc",
  navy: "#253143",
  blue: "#7795ae",
  skyway: "#a7bdca",
  celestial: "#89aabd",
  grey: "#a7a7a2",
  gray: "#a7a7a2",
  pine: "#3d5448",
  green: "#60745f",
  earth: "#766457",
};

const normaliseList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  return value ? [value] : [];
};

const getOptionName = (option) =>
  typeof option === "object" && option !== null
    ? option.name || option.label || option.value || ""
    : String(option || "");

const getColourValue = (option) => {
  if (typeof option === "object" && option !== null && option.hex) {
    return option.hex;
  }

  const colourName = getOptionName(option).toLowerCase();
  const match = Object.entries(COLOUR_VALUES).find(([name]) =>
    colourName.includes(name)
  );

  if (match) return match[1];

  const hue = [...colourName].reduce(
    (total, character) => total + character.charCodeAt(0),
    0
  );
  return `hsl(${hue % 360} 16% 58%)`;
};

const getFallbackSizes = (category = "") => {
  const categoryName = category.toLowerCase();

  if (
    /shoe|sneaker|loafer|derby|sandal|cupsole|athletic/.test(
      categoryName
    )
  ) {
    return Array.from({ length: 12 }, (_, index) => String(index + 35));
  }

  if (/shirt|polo|knit|jacket|hoodie|sweat|denim|pant|short/.test(categoryName)) {
    return ["XS", "S", "M", "L", "XL"];
  }

  return ["ONE SIZE"];
};

const currencyCode =
  siteConfig.currencyCode ||
  (/^[A-Z]{3}$/.test(siteConfig.currency || "")
    ? siteConfig.currency
    : "EUR");

const formatPrice = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "";

  try {
    return new Intl.NumberFormat(siteConfig.locale || "en-NL", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${siteConfig.currencySymbol || siteConfig.currency || ""}${amount}`;
  }
};

const getAttributeValue = (attributes, names) => {
  if (!attributes || Array.isArray(attributes) || typeof attributes !== "object") {
    return "";
  }

  const wantedNames = names.map((name) =>
    name.toLowerCase().replace(/[^a-z0-9]/g, "")
  );
  const match = Object.entries(attributes).find(([key]) =>
    wantedNames.includes(key.toLowerCase().replace(/[^a-z0-9]/g, ""))
  );
  return match ? match[1] : "";
};

const renderUspIcon = (icon) => {
  const commonProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.4",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  if (icon === "shipping") {
    return (
      <svg {...commonProps}>
        <path d="M2.5 6.5h12v9h-12zM14.5 10h3.3l3.7 3.5v2h-7z" />
        <circle cx="6" cy="17.5" r="1.5" />
        <circle cx="18" cy="17.5" r="1.5" />
      </svg>
    );
  }

  if (icon === "points") {
    return (
      <svg {...commonProps}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="m12 7.8 1.3 2.7 3 .4-2.2 2.1.5 3-2.6-1.4L9.4 16l.5-3-2.2-2.1 3-.4z" />
      </svg>
    );
  }

  if (icon === "trusted") {
    return (
      <svg {...commonProps}>
        <path d="M12 3.2 19 6v5.1c0 4.4-2.8 7.7-7 9.7-4.2-2-7-5.3-7-9.7V6z" />
        <path d="m8.8 12 2.1 2.1 4.4-4.5" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="m12 2.8 2.2 5 5 2.2-5 2.2-2.2 5-2.2-5-5-2.2 5-2.2z" />
      <path d="m18.3 15.4.9 2 .2.1 2 .9-2 .9-.2.1-.9 2-.9-2-.2-.1-2-.9 2-.9z" />
    </svg>
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const cart = useContext(CartContext) || {};
  const {
    addToCart,
    cartItems = [],
    toggleWishlist,
    isWishlisted,
    isInWishlist,
  } = cart;

  const product = useMemo(
    () => productData.find((item) => String(item.id) === String(id)),
    [id]
  );

  const images = useMemo(() => {
    if (!product) return [];
    const availableImages = normaliseList(product.images);
    if (product.image) availableImages.unshift(product.image);
    return [...new Set(availableImages)];
  }, [product]);

  const colours = useMemo(
    () => normaliseList(product?.colors),
    [product]
  );
  const sizes = useMemo(() => {
    if (!product) return [];
    const availableSizes = normaliseList(product.size).map(getOptionName);
    return availableSizes.length
      ? availableSizes
      : getFallbackSizes(product.category);
  }, [product]);

  const defaultColour = getOptionName(colours[0]);
  const defaultSize =
    sizes.length === 1 && /one|os/i.test(sizes[0]) ? sizes[0] : "";
  const [selectedColour, setSelectedColour] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [isAdded, setIsAdded] = useState(false);

  useEffect(() => {
    setSelectedColour(defaultColour);
    setSelectedSize(defaultSize);
    setSizeGuideOpen(false);
    setValidationMessage("");
    setIsAdded(false);
  }, [defaultColour, defaultSize, product?.id]);

  useEffect(() => {
    if (!product) return undefined;
    const previousTitle = document.title;
    document.title = `${product.name} | ${siteConfig.brandName}`;
    return () => {
      document.title = previousTitle;
    };
  }, [product]);

  const recentlyViewed = useMemo(() => {
    if (!product) return [];

    const related = productData.filter(
      (item) =>
        String(item.id) !== String(product.id) &&
        item.category === product.category
    );
    const remaining = productData.filter(
      (item) =>
        String(item.id) !== String(product.id) &&
        !related.some((relatedItem) =>
          String(relatedItem.id) === String(item.id)
        )
    );
    return [...related, ...remaining].slice(0, 4);
  }, [product]);

  if (!product) {
    return (
      <main className="product-detail product-detail--empty" id="main-content" tabIndex="-1">
        <section className="product-detail__not-found" aria-labelledby="not-found-title">
          <p>404</p>
          <h1 id="not-found-title">Product not found</h1>
          <p>The piece you are looking for is no longer available.</p>
          <Link to="/shop">DISCOVER THE COLLECTION</Link>
        </section>
      </main>
    );
  }

  const descriptions = normaliseList(product.description);
  const features = normaliseList(product.features || product.Features);
  const attributes =
    product.attributes &&
    !Array.isArray(product.attributes) &&
    typeof product.attributes === "object"
      ? product.attributes
      : {};
  const attributeDetails = Object.entries(attributes).filter(
    ([key, value]) =>
      value && !/fit|size/i.test(key) && typeof value !== "object"
  );
  const sizeAdvice =
    product.sizeAdvice ||
    getAttributeValue(attributes, ["fit", "size and fit", "sizeFit"]) ||
    (/shoe|sneaker|loafer|cupsole|athletic/i.test(product.category || "")
      ? "Runs true to size. If you are between sizes, choose the smaller size."
      : "Designed for a regular fit. Choose your usual size.");
  const compareAtPrice = Number(product.compareAtPrice);
  const hasDiscount =
    Number.isFinite(compareAtPrice) && compareAtPrice > Number(product.price);
  const merchandisingStatus = String(product.status || "");
  const stockLevel = Number(product.stock);
  const hasStockLevel = Number.isFinite(stockLevel);
  const isUnavailable =
    /sold out|out of stock|restock/i.test(merchandisingStatus) ||
    (hasStockLevel && stockLevel <= 0);
  const inventoryStatus = isUnavailable
    ? merchandisingStatus || "OUT OF STOCK"
    : /almost|low stock/i.test(merchandisingStatus)
      ? merchandisingStatus
      : hasStockLevel && stockLevel <= 5
        ? "ALMOST SOLD OUT"
        : "IN STOCK — READY TO SHIP";
  const selectedVariantQuantity = cartItems
    .filter(
      (item) =>
        String(item.id) === String(product.id) &&
        (item.size || "") === (selectedSize || "") &&
        (item.color || "") === (selectedColour || "")
    )
    .reduce((total, item) => total + Number(item.quantity || 0), 0);
  const stockLimitReached =
    hasStockLevel &&
    Boolean(selectedSize) &&
    selectedVariantQuantity >= stockLevel;
  const wishlistChecker = isWishlisted || isInWishlist;
  const wished = wishlistChecker ? wishlistChecker(product.id) : false;
  const shippingThreshold =
    siteConfig.freeShippingThreshold ||
    siteConfig.shipping?.freeThreshold ||
    150;
  const uspItems = [
    {
      icon: "shipping",
      text: `Free shipping from ${formatPrice(shippingThreshold)}`,
    },
    { icon: "points", text: "Earn loyalty points with every purchase" },
    { icon: "trusted", text: "Trusted for over 15 years" },
    { icon: "craft", text: "Handcrafted from premium materials" },
  ];

  const selectSize = (size) => {
    setSelectedSize(size);
    setValidationMessage("");
    setIsAdded(false);
  };

  const selectColour = (colour) => {
    setSelectedColour(colour);
    setIsAdded(false);
  };

  const handleAddToCart = (event) => {
    event.preventDefault();

    if (!selectedSize) {
      setValidationMessage("Please select your size.");
      return;
    }

    if (typeof addToCart === "function") {
      addToCart(product.id, 1, selectedSize, selectedColour);
      setIsAdded(true);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <main className="product-detail" id="main-content" tabIndex="-1">
      <article className="product-detail__product" aria-labelledby="product-title">
        <section
          className={`product-detail__gallery${images.length === 1 ? " is-single" : ""}`}
          aria-label={`${product.name} image gallery`}
        >
          {images.length ? (
            images.map((image, index) => (
              <figure className="product-detail__image" key={`${image}-${index}`}>
                <img
                  src={image}
                  alt={`${product.name}${selectedColour ? ` in ${selectedColour}` : ""}, view ${index + 1}`}
                  loading={index === 0 ? "eager" : "lazy"}
                  decoding="async"
                />
              </figure>
            ))
          ) : (
            <div className="product-detail__image-placeholder">
              <span>Image coming soon</span>
            </div>
          )}
        </section>

        <aside className="product-detail__buy-panel">
          <div className="product-detail__sticky">
            <nav className="product-detail__breadcrumbs" aria-label="Breadcrumb">
              <Link to="/shop">SHOP</Link>
              <span aria-hidden="true">/</span>
              <span>{product.category}</span>
            </nav>

            <header className="product-detail__heading">
              <p className="product-detail__brand">{siteConfig.brandName}</p>
              {merchandisingStatus && !isUnavailable && (
                <span className="product-detail__badge">{merchandisingStatus}</span>
              )}
              <h1 id="product-title">{product.name}</h1>
              <p className={`product-detail__price${hasDiscount ? " is-sale" : ""}`}>
                <span>{formatPrice(product.price)}</span>
                {hasDiscount && (
                  <del aria-label={`Original price ${formatPrice(compareAtPrice)}`}>
                    {formatPrice(compareAtPrice)}
                  </del>
                )}
              </p>
            </header>

            <form className="product-detail__form" onSubmit={handleAddToCart} noValidate>
              {colours.length > 0 && (
                <fieldset className="product-detail__option-group product-detail__colours">
                  <legend>
                    COLOUR: <span>{selectedColour}</span>
                  </legend>
                  <div className="product-detail__colour-list">
                    {colours.map((colour, index) => {
                      const colourName = getOptionName(colour);
                      return (
                        <button
                          className={`product-detail__colour-option${selectedColour === colourName ? " is-selected" : ""}`}
                          key={`${colourName}-${index}`}
                          type="button"
                          aria-pressed={selectedColour === colourName}
                          onClick={() => selectColour(colourName)}
                        >
                          <span
                            className="product-detail__swatch"
                            style={{ backgroundColor: getColourValue(colour) }}
                            aria-hidden="true"
                          />
                          <span>{colourName}</span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
              )}

              <fieldset
                className={`product-detail__option-group product-detail__sizes${validationMessage ? " has-error" : ""}`}
                aria-describedby={validationMessage ? "size-error" : undefined}
              >
                <legend>
                  <span>SIZE{selectedSize ? `: ${selectedSize}` : ":"}</span>
                  <button
                    type="button"
                    aria-expanded={sizeGuideOpen}
                    onClick={() => setSizeGuideOpen((isOpen) => !isOpen)}
                  >
                    FIND YOUR SIZE
                  </button>
                </legend>
                <div className="product-detail__size-list">
                  {sizes.map((size) => (
                    <button
                      className={selectedSize === size ? "is-selected" : ""}
                      key={size}
                      type="button"
                      aria-pressed={selectedSize === size}
                      onClick={() => selectSize(size)}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                {validationMessage && (
                  <p className="product-detail__error" id="size-error" role="alert">
                    {validationMessage}
                  </p>
                )}
                {sizeGuideOpen && (
                  <div className="product-detail__size-guide">
                    <strong>FIT ADVICE</strong>
                    <p>{sizeAdvice}</p>
                  </div>
                )}
                <p className="product-detail__fit-note">{sizeAdvice}</p>
              </fieldset>

              <p
                className={`product-detail__stock${isUnavailable ? " is-unavailable" : ""}`}
              >
                <span aria-hidden="true" />
                {inventoryStatus}
              </p>

              <div className="product-detail__actions">
                <button
                  className="product-detail__add"
                  type="submit"
                  disabled={isUnavailable || stockLimitReached}
                >
                  {isUnavailable
                    ? "CURRENTLY UNAVAILABLE"
                    : stockLimitReached
                      ? "MAXIMUM IN BAG"
                    : isAdded
                      ? "ADDED TO BAG"
                      : "ADD TO BAG"}
                </button>
                <button
                  className={`product-detail__wishlist${wished ? " is-active" : ""}`}
                  type="button"
                  aria-pressed={wished}
                  aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
                  onClick={() => toggleWishlist && toggleWishlist(product.id)}
                >
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20.8 4.7a5.4 5.4 0 0 0-7.7 0L12 5.8l-1.1-1.1a5.4 5.4 0 0 0-7.7 7.7l1.1 1.1L12 21l7.7-7.5 1.1-1.1a5.4 5.4 0 0 0 0-7.7Z" />
                  </svg>
                </button>
              </div>

              {isAdded && (
                <p className="product-detail__added" role="status">
                  Added to your bag. <Link to="/cartpage">VIEW BAG</Link>
                </p>
              )}
            </form>

            <ul className="product-detail__usps" aria-label="Shopping benefits">
              {uspItems.map((item) => (
                <li key={item.text}>
                  {renderUspIcon(item.icon)}
                  <span>{item.text}</span>
                </li>
              ))}
            </ul>

            <div className="product-detail__accordions">
              <details open>
                <summary>DESCRIPTION</summary>
                <div className="product-detail__accordion-content">
                  {descriptions.length ? (
                    descriptions.map((description, index) => (
                      <p key={`${description}-${index}`}>{description}</p>
                    ))
                  ) : (
                    <p>A considered everyday piece made for lasting wear.</p>
                  )}
                </div>
              </details>

              <details>
                <summary>DETAILS</summary>
                <div className="product-detail__accordion-content">
                  {product.material || features.length || attributeDetails.length ? (
                    <ul>
                      {product.material && <li>Material: {product.material}</li>}
                      {product.gender && <li>Designed for: {product.gender}</li>}
                      {features.map((feature, index) => (
                        <li key={`${feature}-${index}`}>{feature}</li>
                      ))}
                      {attributeDetails.map(([key, value]) => (
                        <li key={key}>
                          {key.replace(/([A-Z])/g, " $1")}: {String(value)}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p>Designed with premium materials and a considered finish.</p>
                  )}
                </div>
              </details>

              <details>
                <summary>SIZE &amp; FIT</summary>
                <div className="product-detail__accordion-content">
                  <p>{sizeAdvice}</p>
                  <p>Available in {sizes.join(", ")}.</p>
                </div>
              </details>

              <details>
                <summary>DELIVERY, EXCHANGES AND RETURNS</summary>
                <div className="product-detail__accordion-content">
                  <p>
                    Fast and secure global shipping. Complimentary delivery is
                    available on orders over {formatPrice(shippingThreshold)}.
                  </p>
                  <p>
                    Returns and size exchanges are accepted within 30 days of
                    delivery. Please keep the item unworn and in its original packaging.
                  </p>
                  <Link to="/shipping">READ OUR SHIPPING POLICY</Link>
                </div>
              </details>
            </div>
          </div>
        </aside>
      </article>

      {recentlyViewed.length > 0 && (
        <section className="product-detail__recent" aria-labelledby="recent-heading">
          <div className="product-detail__recent-heading">
            <h2 id="recent-heading">Recently viewed</h2>
            <Link to="/shop">VIEW ALL</Link>
          </div>
          <div className="product-detail__recent-grid">
            {recentlyViewed.map((item) => {
              const itemImage = normaliseList(item.images)[0] || item.image;
              const itemComparePrice = Number(item.compareAtPrice);
              const itemIsDiscounted =
                Number.isFinite(itemComparePrice) &&
                itemComparePrice > Number(item.price);

              return (
                <article className="product-detail__recent-card" key={item.id}>
                  <Link
                    className="product-detail__recent-image"
                    to={`/shopDetails/${item.id}`}
                    onClick={scrollToTop}
                    aria-label={`View ${item.name}`}
                  >
                    {itemImage ? (
                      <img
                        src={itemImage}
                        alt={item.name}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <span>Image coming soon</span>
                    )}
                    {item.status && (
                      <span className="product-detail__recent-badge">
                        {item.status}
                      </span>
                    )}
                  </Link>
                  <div className="product-detail__recent-copy">
                    <h3>
                      <Link to={`/shopDetails/${item.id}`} onClick={scrollToTop}>
                        {item.name}
                      </Link>
                    </h3>
                    <p className={itemIsDiscounted ? "is-sale" : ""}>
                      <span>{formatPrice(item.price)}</span>
                      {itemIsDiscounted && <del>{formatPrice(itemComparePrice)}</del>}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
